import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Plus, Warehouse, AlertTriangle, Trash2, Download, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';
import { generateReportPDF } from '../lib/pdf';
import EditEntityDialog from '../components/EditEntityDialog';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'];

export default function StockManagement() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const { symbol, company } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newItem, setNewItem] = useState({
    item_name: '', category: 'general', unit: 'pcs', current_quantity: 0, minimum_quantity: 0, unit_price: 0, location: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock-items`, { withCredentials: true });
      setItems(r.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/stock-items`, {
        ...newItem,
        current_quantity: parseFloat(newItem.current_quantity) || 0,
        minimum_quantity: parseFloat(newItem.minimum_quantity) || 0,
        unit_price: parseFloat(newItem.unit_price) || 0
      }, { withCredentials: true });
      toast.success('Stock item added');
      setShowNew(false);
      setNewItem({ item_name: '', category: 'general', unit: 'pcs', current_quantity: 0, minimum_quantity: 0, unit_price: 0, location: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API}/stock-items/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const adjustQty = async (id, delta, current) => {
    try {
      await axios.put(`${API}/stock-items/${id}`, { current_quantity: Math.max(0, current + delta) }, { withCredentials: true });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const lowStock = (i) => i.current_quantity <= i.minimum_quantity;

  const totalValue = items.reduce((sum, i) => sum + ((i.current_quantity || 0) * (i.unit_price || 0)), 0);
  const lowStockCount = items.filter(lowStock).length;

  const downloadStockRegister = () => {
    const rows = items.map(i => {
      const value = (i.current_quantity || 0) * (i.unit_price || 0);
      return [
        i.item_name || '-',
        i.category || '-',
        i.location || '-',
        `${i.current_quantity || 0} ${i.unit || ''}`,
        `${i.minimum_quantity || 0}`,
        `${symbol} ${(i.unit_price || 0).toFixed(2)}`,
        `${symbol} ${value.toFixed(2)}`,
        lowStock(i) ? 'LOW STOCK' : 'OK'
      ];
    });
    generateReportPDF({
      title: 'Stock Register',
      subtitle: `Materials inventory for audit and reconciliation as of ${new Date().toLocaleDateString()}`,
      company,
      sections: [
        {
          heading: 'Summary',
          head: ['Metric', 'Value'],
          rows: [
            ['Total stock items', String(items.length)],
            ['Low-stock items', String(lowStockCount)],
            ['Total inventory value', `${symbol} ${totalValue.toFixed(2)}`]
          ]
        },
        {
          heading: 'Materials Register',
          head: ['Item', 'Category', 'Location', 'Qty on Hand', 'Min Qty', 'Unit Cost', 'Total Value', 'Status'],
          rows
        }
      ],
      filename: `stock_register_${new Date().toISOString().split('T')[0]}.pdf`
    });
    toast.success('Stock register PDF generated');
  };

  return (
    <Layout>
      <div data-testid="stock-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('stock.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('stock.subtitle')} · {t('stock.totalValue')}: <strong className="text-teal-700">{symbol} {totalValue.toFixed(2)}</strong></p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/stock-movements" data-testid="view-audit-log-link">
              <Button variant="outline"><Activity className="w-4 h-4 mr-1" />Audit Log</Button>
            </Link>
            <Button data-testid="download-stock-register" variant="outline" onClick={downloadStockRegister} disabled={!items.length}>
              <Download className="w-4 h-4 mr-1" />Stock Register PDF
            </Button>
            {MANAGE.includes(user?.role) && (
              <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogTrigger asChild>
                  <Button data-testid="new-stock-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Item</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Stock Item</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div><Label>Item name</Label><Input data-testid="stock-name-input" value={newItem.item_name} onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Category</Label><Input value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} /></div>
                      <div><Label>Unit</Label><Input value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} placeholder="pcs, kg, ltr" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Current Qty</Label><Input type="number" value={newItem.current_quantity} onChange={(e) => setNewItem({ ...newItem, current_quantity: e.target.value })} /></div>
                      <div><Label>Min Qty</Label><Input type="number" value={newItem.minimum_quantity} onChange={(e) => setNewItem({ ...newItem, minimum_quantity: e.target.value })} /></div>
                      <div><Label>Unit Price ({symbol})</Label><Input type="number" data-testid="stock-cost-input" value={newItem.unit_price} onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })} /></div>
                    </div>
                    <div><Label>Location</Label><Input value={newItem.location} onChange={(e) => setNewItem({ ...newItem, location: e.target.value })} placeholder="e.g., Warehouse A, Shelf 3" /></div>
                    <Button data-testid="submit-stock-btn" onClick={handleCreate} disabled={!newItem.item_name} className="w-full bg-teal-600 hover:bg-teal-700">Add Item</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((i) => {
              const low = lowStock(i);
              return (
                <Card key={i.id} data-testid={`stock-card-${i.id}`} className={`p-5 ${low ? 'border-l-4 border-l-amber-500' : ''}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Warehouse className="w-4 h-4 text-teal-600" />
                      <h3 className="font-semibold text-foreground">{i.item_name}</h3>
                    </div>
                    {low && <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold"><AlertTriangle className="w-3 h-3" />Low</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{i.category} · {i.location}</p>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-2xl font-bold text-foreground">{i.current_quantity}<span className="text-sm font-normal text-slate-500"> {i.unit}</span></div>
                      <div className="text-xs text-muted-foreground">Min: {i.minimum_quantity} {i.unit}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button data-testid={`decrease-stock-${i.id}`} onClick={() => adjustQty(i.id, -1, i.current_quantity)} size="sm" variant="outline">-</Button>
                      <Button data-testid={`increase-stock-${i.id}`} onClick={() => adjustQty(i.id, 1, i.current_quantity)} size="sm" variant="outline">+</Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                    <div className="text-xs">
                      <span className="text-muted-foreground">Unit: </span>
                      <span className="text-slate-900 font-medium">{formatCurrency(i.unit_price, symbol)}</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-muted-foreground">Value: </span>
                      <span className="text-teal-700 font-bold">{formatCurrency((i.current_quantity || 0) * (i.unit_price || 0), symbol)}</span>
                    </div>
                    {MANAGE.includes(user?.role) && (
                      <div className="flex items-center gap-1">
                        <EditEntityDialog
                          item={i}
                          endpoint="stock-items"
                          title="Edit Stock Item"
                          onSaved={fetchData}
                          fields={[
                            { key: 'name', label: 'Item Name' },
                            { key: 'category', label: 'Category' },
                            { key: 'unit', label: 'Unit (e.g. pcs, kg)' },
                            { key: 'current_quantity', label: 'Current Quantity', type: 'number' },
                            { key: 'minimum_quantity', label: 'Minimum Quantity', type: 'number' },
                            { key: 'unit_price', label: 'Unit Price', type: 'number' },
                          ]}
                        />
                        <Button data-testid={`delete-stock-${i.id}`} onClick={() => handleDelete(i.id)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
            {items.length === 0 && (
              <Card className="p-10 text-center col-span-full">
                <Warehouse className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No stock items</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
